import React, { useState } from 'react';
import { PlusIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, UsersIcon, UserPlusIcon, AlertTriangleIcon, ClockIcon, CheckCircleIcon, CalendarIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, ChevronRightIcon, FilterIcon, BellIcon, CheckIcon, XIcon } from 'lucide-react';
import { RequisitionList } from './procurement/RequisitionList';
import { PurchaseOrderList } from './procurement/PurchaseOrderList';
import { formatCurrency } from './utils/currencyFormatter';
import { Remi } from './ai/Remi';
export function Dashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for new dashboard sections
  const vendorStats = {
    pendingOnboarding: 5,
    failingCompliance: 3,
    awaitingReview: 7
  };
  const invoiceStats = {
    inProcessing: 12,
    failingCompliance: 4,
    approvedForPayment: 8
  };
  const vendorsNeedingAction = [{
    id: 'VEN-2023-004',
    name: 'Global Supply Co.',
    status: 'Incomplete',
    issue: 'Missing tax information',
    dueDate: '2023-11-15'
  }, {
    id: 'VEN-2023-005',
    name: 'Tech Solutions Inc.',
    status: 'Flagged',
    issue: 'Insurance certificate expired',
    dueDate: '2023-11-10'
  }, {
    id: 'VEN-2023-006',
    name: 'Office Essentials',
    status: 'Awaiting Review',
    issue: 'New onboarding submission',
    dueDate: '2023-11-20'
  }];
  const invoicesNeedingAttention = [{
    id: 'INV-2023-001',
    invoiceNumber: 'INV-001234',
    vendor: 'Dell Technologies',
    issue: 'Unmatched PO',
    status: 'Error',
    amount: 4500.0,
    currency: 'USD',
    dueDate: '2023-11-12'
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'INV-005678',
    vendor: 'Office Depot',
    issue: 'Missing GRN',
    status: 'Warning',
    amount: 1250.75,
    currency: 'USD',
    dueDate: '2023-11-15'
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'INV-009012',
    vendor: 'PrintWorks Inc.',
    issue: 'Currency mismatch',
    status: 'Error',
    amount: 3200.5,
    currency: 'EUR',
    dueDate: '2023-11-18'
  }];
  const upcomingRenewals = [{
    id: 'DOC-2023-001',
    vendor: 'Dell Technologies',
    documentType: 'Insurance Certificate',
    expiryDate: '2023-11-30',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-002',
    vendor: 'Global Supply Co.',
    documentType: 'Tax Compliance',
    expiryDate: '2023-12-05',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-003',
    vendor: 'Tech Solutions Inc.',
    documentType: 'Business License',
    expiryDate: '2023-12-15',
    status: 'Expiring Soon'
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-3719">
      <div className="mb-8 flex justify-between items-center" data-id="element-3720">
        <div data-id="element-3721">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3722">
            P2P Control Centre
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3723">
            Comprehensive overview of procurement, vendor management, and
            accounts payable
          </p>
        </div>
        <div className="flex items-center" data-id="element-3724">
          <div className="flex items-center" data-id="element-3725">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3726" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3727">
              <option value="7days" data-id="element-3728">Last 7 days</option>
              <option value="30days" data-id="element-3729">Last 30 days</option>
              <option value="90days" data-id="element-3730">Last 90 days</option>
              <option value="ytd" data-id="element-3731">Year to date</option>
            </select>
          </div>
        </div>
      </div>
      {/* Remi AI Assistant */}
      <div className="mb-8" data-id="element-3732">
        <Remi setCurrentView={setCurrentView} data-id="element-3733" />
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-3734">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3735">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3736">
          {/* Existing cards */}
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3737">
            <div className="p-5" data-id="element-3738">
              <div className="flex items-center" data-id="element-3739">
                <div className="flex-shrink-0" data-id="element-3740">
                  <FileTextIcon className="h-6 w-6 text-gray-400" data-id="element-3741" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3742">
                  <dl data-id="element-3743">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3744">
                      Total Requisitions
                    </dt>
                    <dd data-id="element-3745">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3746">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3747">
            <div className="p-5" data-id="element-3748">
              <div className="flex items-center" data-id="element-3749">
                <div className="flex-shrink-0" data-id="element-3750">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-3751" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3752">
                  <dl data-id="element-3753">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3754">
                      Pending Approval
                    </dt>
                    <dd data-id="element-3755">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3756">
                        {pendingApproval}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-3757">
            <div className="p-5" data-id="element-3758">
              <div className="flex items-center" data-id="element-3759">
                <div className="flex-shrink-0" data-id="element-3760">
                  <ClipboardCheckIcon className="h-6 w-6 text-indigo-400" data-id="element-3761" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3762">
                  <dl data-id="element-3763">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3764">
                      Purchase Orders
                    </dt>
                    <dd data-id="element-3765">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3766">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-3767">
            <div className="p-5" data-id="element-3768">
              <div className="flex items-center" data-id="element-3769">
                <div className="flex-shrink-0" data-id="element-3770">
                  <TrendingUpIcon className="h-6 w-6 text-green-400" data-id="element-3771" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3772">
                  <dl data-id="element-3773">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3774">
                      Total Spend
                    </dt>
                    <dd data-id="element-3775">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3776">
                        {formatCurrency(totalReqSpend + totalPOSpend, 'USD', locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Vendor Status Summary Cards */}
      <div className="mb-8" data-id="element-3777">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3778">
          Vendor Management
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3779">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3780">
            <div className="p-5" data-id="element-3781">
              <div className="flex items-center" data-id="element-3782">
                <div className="flex-shrink-0" data-id="element-3783">
                  <UserPlusIcon className="h-6 w-6 text-yellow-400" data-id="element-3784" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3785">
                  <dl data-id="element-3786">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3787">
                      Vendors Pending Onboarding
                    </dt>
                    <dd data-id="element-3788">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3789">
                        {vendorStats.pendingOnboarding}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('compliance-check')} data-id="element-3790">
            <div className="p-5" data-id="element-3791">
              <div className="flex items-center" data-id="element-3792">
                <div className="flex-shrink-0" data-id="element-3793">
                  <AlertTriangleIcon className="h-6 w-6 text-orange-400" data-id="element-3794" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3795">
                  <dl data-id="element-3796">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3797">
                      <span className="group relative" data-id="element-3798">
                        Vendors Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3799">
                          Includes expired insurance, missing tax details, etc.
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3800">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3801">
                        {vendorStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3802">
            <div className="p-5" data-id="element-3803">
              <div className="flex items-center" data-id="element-3804">
                <div className="flex-shrink-0" data-id="element-3805">
                  <ClockIcon className="h-6 w-6 text-blue-400" data-id="element-3806" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3807">
                  <dl data-id="element-3808">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3809">
                      Vendors Awaiting Review
                    </dt>
                    <dd data-id="element-3810">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3811">
                        {vendorStats.awaitingReview}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice Summary Cards */}
      <div className="mb-8" data-id="element-3812">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3813">
          Accounts Payable
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3814">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3815">
            <div className="p-5" data-id="element-3816">
              <div className="flex items-center" data-id="element-3817">
                <div className="flex-shrink-0" data-id="element-3818">
                  <ReceiptIcon className="h-6 w-6 text-purple-400" data-id="element-3819" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3820">
                  <dl data-id="element-3821">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3822">
                      Invoices in Processing
                    </dt>
                    <dd data-id="element-3823">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3824">
                        {invoiceStats.inProcessing}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('matching')} data-id="element-3825">
            <div className="p-5" data-id="element-3826">
              <div className="flex items-center" data-id="element-3827">
                <div className="flex-shrink-0" data-id="element-3828">
                  <AlertCircleIcon className="h-6 w-6 text-red-400" data-id="element-3829" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3830">
                  <dl data-id="element-3831">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3832">
                      <span className="group relative" data-id="element-3833">
                        Invoices Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3834">
                          Unmatched PO, blocked vendor, tax or value mismatches
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3835">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3836">
                        {invoiceStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3837">
            <div className="p-5" data-id="element-3838">
              <div className="flex items-center" data-id="element-3839">
                <div className="flex-shrink-0" data-id="element-3840">
                  <CreditCardIcon className="h-6 w-6 text-green-400" data-id="element-3841" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3842">
                  <dl data-id="element-3843">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3844">
                      Invoices Approved for Payment
                    </dt>
                    <dd data-id="element-3845">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3846">
                        {invoiceStats.approvedForPayment}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3847">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3848">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3849">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3850">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3851">
              View all
            </button>
          </div>
          <RequisitionList requisitions={recentRequisitions} locale={locale} data-id="element-3852" />
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3853">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3854">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3855">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3856">
              View all
            </button>
          </div>
          <PurchaseOrderList purchaseOrders={recentPurchaseOrders} locale={locale} data-id="element-3857" />
        </div>
      </div>
      {/* New Dashboard Sections */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3858">
        {/* Vendors Needing Action */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3859">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3860">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3861">
              Vendors Needing Action
            </h3>
            <button onClick={() => setCurrentView('vendor-management')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3862">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3863">
            {vendorsNeedingAction.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3864">
                No vendors need attention at this time.
              </li> : vendorsNeedingAction.map(vendor => <li key={vendor.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('vendor-management')} data-id="element-3865">
                  <div className="flex items-center justify-between" data-id="element-3866">
                    <div className="flex items-center" data-id="element-3867">
                      <div className="flex-shrink-0" data-id="element-3868">
                        {vendor.status === 'Incomplete' ? <XIcon className="h-5 w-5 text-red-400" data-id="element-3869" /> : vendor.status === 'Flagged' ? <AlertTriangleIcon className="h-5 w-5 text-orange-400" data-id="element-3870" /> : <ClockIcon className="h-5 w-5 text-blue-400" data-id="element-3871" />}
                      </div>
                      <div className="ml-3" data-id="element-3872">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3873">
                          {vendor.name}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-3874">{vendor.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-3875">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-3876">
                        Due: {vendor.dueDate}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('vendor-management');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3877">
                        Review
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
        {/* Invoices Needing Attention */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3878">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3879">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3880">
              Invoices Needing Attention
            </h3>
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3881">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3882">
            {invoicesNeedingAttention.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3883">
                No invoices need attention at this time.
              </li> : invoicesNeedingAttention.map(invoice => <li key={invoice.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('invoice-list')} data-id="element-3884">
                  <div className="flex items-center justify-between" data-id="element-3885">
                    <div className="flex items-center" data-id="element-3886">
                      <div className="flex-shrink-0" data-id="element-3887">
                        {invoice.status === 'Error' ? <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-3888" /> : <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-3889" />}
                      </div>
                      <div className="ml-3" data-id="element-3890">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3891">
                          {invoice.invoiceNumber} - {invoice.vendor}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-3892">{invoice.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-3893">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-3894">
                        {formatCurrency(invoice.amount, invoice.currency, locale)}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('matching');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3895">
                        Resolve
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>
      {/* Upcoming Renewals / Expiries */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-3896">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3897">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3898">
            Upcoming Renewals & Expiries
          </h3>
          <button onClick={() => setCurrentView('compliance-check')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3899">
            View all
          </button>
        </div>
        <ul className="divide-y divide-gray-200" data-id="element-3900">
          {upcomingRenewals.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3901">
              No upcoming renewals or expiries.
            </li> : upcomingRenewals.map(renewal => <li key={renewal.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('compliance-check')} data-id="element-3902">
                <div className="flex items-center justify-between" data-id="element-3903">
                  <div className="flex items-center" data-id="element-3904">
                    <div className="flex-shrink-0" data-id="element-3905">
                      <CalendarIcon className="h-5 w-5 text-orange-400" data-id="element-3906" />
                    </div>
                    <div className="ml-3" data-id="element-3907">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3908">
                        {renewal.vendor} - {renewal.documentType}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3909">
                        Expires on {renewal.expiryDate}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-3910">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-3911">
                      <ClockIcon className="mr-1 h-3 w-3" data-id="element-3912" />
                      {renewal.status}
                    </span>
                  </div>
                </div>
              </li>)}
        </ul>
      </div>
      {/* Optional: Vendor Compliance Trend Chart */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8 p-5" data-id="element-3913">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3914">
          Vendor Compliance Trend (Last 3 Months)
        </h3>
        <div className="h-64 flex items-center justify-center" data-id="element-3915">
          <div className="w-full h-full flex items-end justify-around px-10" data-id="element-3916">
            <div className="flex flex-col items-center" data-id="element-3917">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '70%'
            }} data-id="element-3918"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3919">Sep</p>
              <p className="text-xs text-gray-400" data-id="element-3920">85%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-3921">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '82%'
            }} data-id="element-3922"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3923">Oct</p>
              <p className="text-xs text-gray-400" data-id="element-3924">92%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-3925">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '78%'
            }} data-id="element-3926"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3927">Nov</p>
              <p className="text-xs text-gray-400" data-id="element-3928">89%</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}